import './dynamicPriceCalendar.scss';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  DynamicPriceCalendar as DSDynamicPriceCalendar,
  LoadingOverlay,
} from '@eliberty/design-system-react';
import { startOfDay } from 'date-fns';

import { getLocale } from '~helpers';
import { useDynamicPrices } from '~hooks';
import { ConsumerCategoriesPicker } from '~molecules';
import { ConsumerCategory, DynamicPriceDate } from '~types/appsync.types';
import { formatDateISO, normalizeCalendarDate, parseCalendarDate } from '~utils';

type DayPriceInfo = {
  date: Date;
  price: number;
  originalPrice?: number;
};

export interface DynamicPriceCalendarProps {
  /** Product slug for fetching dynamic prices */
  productSlug: string;
  /** List of consumer categories for price filtering */
  consumerCategories: ConsumerCategory[];
  /** Currency for price display */
  currency?: string;
  /** Starting date for price fetching */
  dynamicPricesFromDate: Date | null;
  /** Callback when dynamic prices from date changes */
  onDynamicPricesFromDateChange: (date: Date | null) => void;
  /** Minimum selectable date */
  minDate: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Currently selected date */
  selectedDate?: Date;
  /** Range duration in days for multi-day selection */
  rangeDuration?: number;
  /** Date intervals to exclude from selection */
  excludeDateIntervals?: { start: Date; end: Date }[];
  /** Function to check if a date is selectable */
  isSelectableDate: (date: Date) => boolean;
  /** Callback when a date is selected */
  onDateSelect: (date: Date | undefined) => void;
  /** Callback when month changes */
  onMonthChange?: (date: Date) => void;
  /** Callback when selected date has a dynamic price discount */
  onDynamicDiscountChange?: (hasDiscount: boolean) => void;
  /** Specific dates to include for selection */
  includeDates?: Date[];
  /** Date to open the calendar to (controls initial displayed month) */
  openToDate?: Date;
}

function getCurrencySymbol(locale: string, currency: string): string {
  try {
    return (
      new Intl.NumberFormat(locale, { currency, style: 'currency' })
        .formatToParts(0)
        .find((p) => p.type === 'currency')?.value ?? currency
    );
  } catch {
    return currency;
  }
}

/**
 * DynamicPriceCalendar integrates the design system DynamicPriceCalendar
 * with dynamic pricing fetched from the API.
 * It shows prices per day and allows filtering by consumer category.
 */
const DynamicPriceCalendar = ({
  productSlug,
  consumerCategories,
  currency = 'EUR',
  dynamicPricesFromDate,
  onDynamicPricesFromDateChange,
  minDate,
  maxDate,
  isSelectableDate,
  excludeDateIntervals = [],
  onMonthChange,
  selectedDate,
  onDateSelect,
  onDynamicDiscountChange,
  rangeDuration,
  includeDates,
  openToDate,
}: DynamicPriceCalendarProps) => {
  const intl = useIntl();
  const locale = getLocale();

  const [selectedConsumerCategory, setSelectedConsumerCategory] = useState<string | null>(null);
  const [executeDynamicPrices, { loading: loadingDynamicPrices }] = useDynamicPrices();
  const [dynamicPrices, setDynamicPrices] = useState<DynamicPriceDate[]>([]);
  const hasFetchedPricesRef = useRef(false);
  const selectableConsumerCategories = useMemo(
    () => consumerCategories.filter((c) => !c.student),
    [consumerCategories],
  );

  // Initialize or reconcile selected consumer category when categories list changes
  useEffect(() => {
    if (selectableConsumerCategories.length === 0) {
      setSelectedConsumerCategory(null);
      return;
    }
    setSelectedConsumerCategory((prev) => {
      if (prev && selectableConsumerCategories.some((c) => c.shortname === prev)) return prev;
      return selectableConsumerCategories[0]?.shortname ?? null;
    });
  }, [selectableConsumerCategories]);

  // Fetch dynamic prices
  useEffect(() => {
    if (!selectedConsumerCategory || !dynamicPricesFromDate) {
      hasFetchedPricesRef.current = false;
      return;
    }
    let active = true;
    hasFetchedPricesRef.current = false;
    executeDynamicPrices({
      variables: {
        consumerCategoryShortname: selectedConsumerCategory,
        fromDate: formatDateISO(dynamicPricesFromDate),
        nbDays: 31,
        productSlug,
      },
    }).then((response) => {
      if (!active) return;
      hasFetchedPricesRef.current = true;
      const list = response?.data?.dynamicPrices || [];
      if (list.length === 0) {
        setDynamicPrices([]);
        return;
      }
      const match = list.find(
        (item) => item?.consumerCategoryShortname === selectedConsumerCategory,
      );
      setDynamicPrices(match?.datesCollection ?? []);
    });
    return () => {
      active = false;
    };
  }, [selectedConsumerCategory, dynamicPricesFromDate, executeDynamicPrices, productSlug]);

  // Adjust dynamicPricesFromDate if outside valid range (min/max only, not exclusions)
  useEffect(() => {
    if (!dynamicPricesFromDate) return;
    const timestamp = startOfDay(dynamicPricesFromDate).getTime();
    const outsideWindow =
      timestamp < startOfDay(minDate).getTime() ||
      (maxDate ? timestamp > startOfDay(maxDate).getTime() : false);
    if (outsideWindow && onDynamicPricesFromDateChange) {
      onDynamicPricesFromDateChange(startOfDay(minDate));
      onMonthChange?.(minDate);
    }
  }, [minDate, maxDate, dynamicPricesFromDate, onDynamicPricesFromDateChange, onMonthChange]);

  // Convert API prices (cents) to DS DayPriceInfo (decimals)
  const dayPrices: DayPriceInfo[] = useMemo(() => {
    if (loadingDynamicPrices) return [];
    return dynamicPrices
      .filter((p) => p.price >= 0 && isSelectableDate(parseCalendarDate(p.date)))
      .map((p) => ({
        date: parseCalendarDate(p.date),
        price: p.price / 100,
        ...(p.basePrice === p.price ? {} : { originalPrice: p.basePrice / 100 }),
      }));
  }, [dynamicPrices, loadingDynamicPrices, isSelectableDate]);

  // Allowed days set used for date invalidation
  const allowedDaysSet = useMemo(() => {
    if (loadingDynamicPrices) return [] as number[];
    return dayPrices.map((p) => startOfDay(p.date).getTime());
  }, [dayPrices, loadingDynamicPrices]);

  const a11yLabels = useMemo(
    () => ({
      bestPrice: intl.formatMessage({
        defaultMessage: 'best price',
        id: 'dynamicPriceCalendar.bestPrice',
      }),
      inRange: intl.formatMessage({
        defaultMessage: 'in selection',
        id: 'dynamicPriceCalendar.inRange',
      }),
      originalPrice: intl.formatMessage({
        defaultMessage: 'original price',
        id: 'dynamicPriceCalendar.originalPrice',
      }),
      selectedDate: intl.formatMessage({
        defaultMessage: 'selected',
        id: 'dynamicPriceCalendar.selectedDate',
      }),
      unavailable: intl.formatMessage({
        defaultMessage: 'not available',
        id: 'dynamicPriceCalendar.unavailable',
      }),
    }),
    [intl],
  );

  const handleMonthChange = useCallback(
    (d: Date) => {
      const newDate = new Date(d.getFullYear(), d.getMonth(), 1);
      onDynamicPricesFromDateChange(newDate);
      onMonthChange?.(d);
    },
    [onMonthChange, onDynamicPricesFromDateChange],
  );

  // Invalidate selected date when not in allowed days of the same month
  useEffect(() => {
    if (
      !selectedDate ||
      loadingDynamicPrices ||
      !hasFetchedPricesRef.current ||
      !dynamicPricesFromDate
    ) {
      return;
    }

    // Only invalidate if the selected date is in the currently loaded month
    const selectedMonth = selectedDate.getMonth();
    const selectedYear = selectedDate.getFullYear();
    const loadedMonth = dynamicPricesFromDate.getMonth();
    const loadedYear = dynamicPricesFromDate.getFullYear();

    if (selectedMonth !== loadedMonth || selectedYear !== loadedYear) {
      return;
    }

    const selectedTimestamp = startOfDay(selectedDate).getTime();
    if (!allowedDaysSet.includes(selectedTimestamp)) {
      onDateSelect(undefined);
    }
  }, [onDateSelect, selectedDate, allowedDaysSet, loadingDynamicPrices, dynamicPricesFromDate]);

  // Notify parent when selected date has a dynamic price discount (basePrice !== price)
  useEffect(() => {
    if (!onDynamicDiscountChange) return;
    if (!selectedDate) {
      onDynamicDiscountChange(false);
      return;
    }

    // Only update discount state when viewing the same month as the selected date;
    // browsing another month must NOT reset the promotion banner.
    if (dynamicPricesFromDate) {
      const sameMonth =
        selectedDate.getMonth() === dynamicPricesFromDate.getMonth() &&
        selectedDate.getFullYear() === dynamicPricesFromDate.getFullYear();
      if (!sameMonth) return;
    }

    if (loadingDynamicPrices) return;

    const selectedDayPrice = dayPrices.find(
      (p) => startOfDay(p.date).getTime() === startOfDay(selectedDate).getTime(),
    );
    onDynamicDiscountChange(
      !!selectedDayPrice?.originalPrice && selectedDayPrice.originalPrice > selectedDayPrice.price,
    );
  }, [
    selectedDate,
    dayPrices,
    loadingDynamicPrices,
    onDynamicDiscountChange,
    dynamicPricesFromDate,
  ]);

  const currencySymbol = useMemo(() => getCurrencySymbol(locale, currency), [locale, currency]);

  const selectedCategoryName = useMemo(() => {
    const cat = selectableConsumerCategories.find((c) => c.shortname === selectedConsumerCategory);
    return (
      cat?.name ??
      intl.formatMessage({
        defaultMessage: 'adult',
        id: 'dynamicPriceCalendar.defaultCategoryName',
      })
    );
  }, [selectableConsumerCategories, selectedConsumerCategory, intl]);

  return (
    <div className='DynamicPriceCalendarWidget'>
      <ConsumerCategoriesPicker
        categories={selectableConsumerCategories}
        value={selectedConsumerCategory}
        onChange={setSelectedConsumerCategory}
        classNamePrefix='DynamicPriceCalendar'
        testIdPrefix='DynamicPriceCalendar'
      />
      <div className='DynamicPriceCalendarWidget_calendarContainer'>
        <LoadingOverlay visible={loadingDynamicPrices} />
        <DSDynamicPriceCalendar
          dayPrices={dayPrices}
          a11yLabels={a11yLabels}
          onClick={(d) => onDateSelect(normalizeCalendarDate(d))}
          onMonthChange={handleMonthChange}
          minDate={minDate}
          maxDate={maxDate}
          selectedDate={selectedDate}
          rangeDuration={rangeDuration}
          excludeDateIntervals={excludeDateIntervals}
          includeDates={includeDates}
          openToDate={openToDate}
          locale={locale}
          chooseDayAriaLabelPrefix={intl.formatMessage({
            defaultMessage: 'Choose',
            id: 'dynamicPriceCalendar.chooseDayAriaLabelPrefix',
          })}
          disabledDayAriaLabelPrefix={intl.formatMessage({
            defaultMessage: 'Not available',
            id: 'dynamicPriceCalendar.disabledDayAriaLabelPrefix',
          })}
          previousMonthAriaLabel={intl.formatMessage({
            defaultMessage: 'Previous month',
            id: 'dynamicPriceCalendar.previousMonthAriaLabel',
          })}
          nextMonthAriaLabel={intl.formatMessage({
            defaultMessage: 'Next month',
            id: 'dynamicPriceCalendar.nextMonthAriaLabel',
          })}
          monthAriaLabelPrefix={intl.formatMessage({
            defaultMessage: 'Calendar',
            id: 'dynamicPriceCalendar.monthAriaLabelPrefix',
          })}
        />
      </div>
      <p className='DynamicPriceCalendarWidget_disclaimer'>
        <FormattedMessage
          id='dynamicPriceCalendar.disclaimer'
          defaultMessage='Prices shown are for 1 {categoryName}, in {currency}, rounded up, may vary with availability, and confirmed when selecting participants.'
          values={{ categoryName: selectedCategoryName.toLowerCase(), currency: currencySymbol }}
        />
      </p>
    </div>
  );
};

export default DynamicPriceCalendar;
